import {
  defaultOutcomeDefinitions,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { Pill } from "@repo/ui/Pills/pill";
import { IconCheck } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { Dialog, GridList, GridListItem } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { getOutcomeCategoryPillColor } from "./outcomeCategoryPill.js";

interface OutcomeTemplateChooserDialogProps {
  isOpen: boolean;
  isReplacingOutcome: boolean;
  initialOutcome?: OutcomeDefinition;
  initialTemplateId?: string;
  onOpenChange: (isOpen: boolean) => void;
  onChoose: (outcome: OutcomeDefinition) => void;
}

/**
 * Keeps templates secondary to the transcript/CRM decision while making the
 * relationship between a template, its values, and its instructions explicit.
 */
export const OutcomeTemplateChooserDialog: FC<
  OutcomeTemplateChooserDialogProps
> = ({
  isOpen,
  isReplacingOutcome,
  initialOutcome,
  initialTemplateId,
  onOpenChange,
  onChoose,
}) => {
  return (
    <CenteredModal
      isDismissable
      isOpen={isOpen}
      size={Size.LARGE}
      onOpenChange={onOpenChange}
    >
      <Dialog className="w-full max-w-2xl px-4 pt-5 pb-4 text-left outline-hidden sm:p-6">
        {({ close }) => (
          <OutcomeTemplateChooserDialogContent
            key={`${initialTemplateId ?? "default"}-${String(isReplacingOutcome)}`}
            initialOutcome={initialOutcome}
            initialTemplateId={initialTemplateId}
            isReplacingOutcome={isReplacingOutcome}
            onClose={close}
            onChoose={(nextOutcome) => {
              onChoose(nextOutcome);
              close();
            }}
          />
        )}
      </Dialog>
    </CenteredModal>
  );
};

interface OutcomeTemplateChooserDialogContentProps {
  isReplacingOutcome: boolean;
  initialOutcome?: OutcomeDefinition;
  initialTemplateId?: string;
  onClose: () => void;
  onChoose: (outcome: OutcomeDefinition) => void;
}

const OutcomeTemplateChooserDialogContent: FC<
  OutcomeTemplateChooserDialogContentProps
> = ({
  isReplacingOutcome,
  initialOutcome,
  initialTemplateId,
  onClose,
  onChoose,
}) => {
  const firstTemplate = defaultOutcomeDefinitions[0];
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | undefined
  >(
    () =>
      defaultOutcomeDefinitions.find(
        (definition) => definition.id === initialTemplateId,
      )?.id ?? firstTemplate?.id,
  );

  const selectedTemplate = defaultOutcomeDefinitions.find(
    (definition) => definition.id === selectedTemplateId,
  );

  return (
    <>
      <ModalHeader onClose={onClose}>Choose an outcome template</ModalHeader>
      <p className="mt-2 text-sm leading-5 text-muted-foreground">
        Each template includes values and instructions Alpharun will use to
        determine the outcome from the call transcript.
        {isReplacingOutcome
          ? " Choosing one will replace your current values and instructions."
          : " You can customize both after choosing."}
      </p>

      <GridList
        aria-label="Outcome template"
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={selectedTemplateId ? [selectedTemplateId] : []}
        onSelectionChange={(keys) => {
          if (keys === "all") return;
          const [selectedKey] = keys;
          if (typeof selectedKey === "string") {
            setSelectedTemplateId(selectedKey);
          }
        }}
        className="-mx-1 mt-4 max-h-[440px] overflow-y-auto px-1 py-1"
      >
        {defaultOutcomeDefinitions.map((definition) => {
          const outcomeToDisplay =
            definition.id === initialOutcome?.id ? initialOutcome : definition;

          return (
            <GridListItem
              key={definition.id}
              id={definition.id}
              textValue={definition.name}
              className={twMerge(
                "group flex cursor-pointer items-start gap-3 border-b border-border-light px-3 py-3 outline-hidden transition-colors first:border-t hover:bg-gray-50 sm:items-center",
                "selected:bg-indigo-50/70",
                "focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset",
              )}
            >
              <span className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <span className="min-w-36 shrink-0 text-sm font-medium text-foreground">
                  {definition.name}
                </span>
                <span className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                  {outcomeToDisplay.results.map((result) => (
                    <Pill
                      key={result.value}
                      label={result.label}
                      color={getOutcomeCategoryPillColor(result.category)}
                      size={Size.SMALL}
                    />
                  ))}
                </span>
              </span>
              <span
                aria-hidden
                className="flex size-5 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-white transition-colors group-selected:border-indigo-600 group-selected:bg-indigo-600"
              >
                <IconCheck className="size-3.5 opacity-0 group-selected:opacity-100" />
              </span>
            </GridListItem>
          );
        })}
      </GridList>

      <div className="mt-4 flex justify-end gap-2 border-t border-border-light pt-4">
        <Button variant={ButtonVariant.Secondary} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          isDisabled={!selectedTemplate}
          onClick={() => {
            if (!selectedTemplate) return;
            onChoose(
              selectedTemplate.id === initialOutcome?.id
                ? initialOutcome
                : selectedTemplate,
            );
          }}
        >
          Use template
        </Button>
      </div>
    </>
  );
};
